import { CallEndRounded, MicOffRounded, MicRounded, VideocamOffRounded, VideocamRounded, VolumeUpRounded } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';
import { chipColor } from '../../theme';
import './call.css';

// The video call takes no space of its own in the side panel, which already
// has to fit the player's hand on screen:
//   CallControls — in the player's own card: Join, or mic / camera / leave.
//   CallThumb    — each person's picture, in place of their chip in the
//                  Players list, next to their name and turn.
// Both are driven by the one useCall() in the Board.

const STATE_LABEL = {
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    failed: "Couldn't connect"
};

export const isInCall = (call) => call.status === 'in-call' || call.status === 'reconnecting';

/**
 * One person's picture. Their audio plays through the same element, so it
 * stays mounted (just covered) while their camera is off.
 */
export const CallThumb = ({ stream, name, team, self, audio, video, state, onBlocked }) => {
    const ref = useRef(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        if (element.srcObject !== stream) {
            element.srcObject = stream || null;
        }
        if (stream && !self) {
            // Browsers may refuse to start sound without a click first. Any
            // other failure (an AbortError when the stream is swapped for one
            // with a new track) sorts itself out on the next play().
            element.play().catch((error) => {
                if (error?.name === 'NotAllowedError') {
                    onBlocked?.();
                }
            });
        }
    }, [stream, self, onBlocked]);

    const showPicture = video && stream?.getVideoTracks().length > 0;
    const label = self ? 'Your camera' : `${name}'s camera`;
    return (
        <span className={`call-thumb${self ? ' self' : ''}`} style={{ '--c': chipColor(team) || '#8A958F' }} title={label}>
            <video ref={ref} autoPlay playsInline muted={self} className={showPicture ? '' : 'hidden'} aria-label={label} />
            {!showPicture && <span className="call-avatar" aria-hidden="true">{(name || '?').charAt(0).toUpperCase()}</span>}
            {!self && state && state !== 'connected' && <span className={`call-state ${state}`}>{STATE_LABEL[state] || ''}</span>}
            {!audio && <span className="call-muted" aria-label="Muted"><MicOffRounded fontSize="inherit" /></span>}
        </span>
    );
};

/** Join, or the controls once in; plus a line of status underneath. */
export const CallControls = ({ call, me, soundBlocked, onUnblockSound }) => {
    if (!call.available && call.status === 'idle') {
        return null;
    }
    const others = call.participants.filter(p => p.userId !== me?.userId);

    if (isInCall(call)) {
        let hint = null;
        if (call.status === 'reconnecting') {
            hint = 'Connection lost, getting you back in…';
        } else if (call.notice) {
            hint = call.notice;
        }
        return (
            <>
                <div className="call-controls" aria-label="Call controls">
                    <Tooltip title={call.audioOn ? 'Mute' : 'Unmute'}>
                        <span>
                            <IconButton size="small" aria-label={call.audioOn ? 'Mute' : 'Unmute'} className={call.audioOn ? '' : 'off'}
                                disabled={!call.hasAudio} onClick={call.toggleAudio}>
                                {call.audioOn ? <MicRounded fontSize="small" /> : <MicOffRounded fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={call.videoOn ? 'Turn camera off' : 'Turn camera on'}>
                        <IconButton size="small" aria-label={call.videoOn ? 'Turn camera off' : 'Turn camera on'} className={call.videoOn ? '' : 'off'}
                            onClick={call.toggleVideo}>
                            {call.videoOn ? <VideocamRounded fontSize="small" /> : <VideocamOffRounded fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Leave call">
                        <IconButton size="small" aria-label="Leave call" className="leave" onClick={call.leave}>
                            <CallEndRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
                {soundBlocked && <Button size="small" startIcon={<VolumeUpRounded />} className="call-sound" onClick={onUnblockSound}>
                    Turn on sound
                </Button>}
                {hint && <p className="call-hint" role="status">{hint}</p>}
            </>
        );
    }

    const full = call.participants.length >= call.max;
    const joining = call.status === 'joining';
    let hint = null;
    if (call.askingPermission) {
        hint = 'Allow your camera and microphone when your browser asks.';
    } else if (call.error || call.notice) {
        hint = call.error || call.notice;
    } else if (others.length > 0) {
        hint = `${others.map(p => p.name).join(', ')} ${others.length === 1 ? 'is' : 'are'} on the call.`;
    }

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                size="small"
                className={`call-join${joining ? ' busy' : ''}`}
                startIcon={joining ? <CircularProgress size={14} color="inherit" /> : <VideocamRounded />}
                disabled={full || joining || !call.subscribed}
                onClick={call.join}
            >
                {full ? 'Call is full' : joining ? (call.askingPermission ? 'Allow camera…' : 'Joining…') : 'Join call'}
            </Button>
            {hint && <p className={`call-hint${call.error ? ' error' : ''}`} role="status">{hint}</p>}
        </>
    );
};
